import { useCallback } from 'react';

import {
  useSuppliersPageVendorPromotionMarketingUpdateMutation,
  type VendorPromotionMarketingTranslationInput,
} from '../../../../../../../generated';
import { type PromotionRow } from '../types';
import { mapPromotionRecord } from '../utils/vendorPromotionMapper';

const DEFAULT_ERROR_MESSAGE = 'Could not save the display copy.';

export type MarketingUpdateResult =
  | { kind: 'success'; promotion: PromotionRow }
  | { kind: 'error'; message: string };

interface UseVendorPromotionMarketingUpdateParams {
  vendorId?: string;
}

interface UseVendorPromotionMarketingUpdateResult {
  save: (
    promotionId: string,
    translations: VendorPromotionMarketingTranslationInput[]
  ) => Promise<MarketingUpdateResult>;
  isSaving: boolean;
}

/**
 * Writes the display copy of one promotion. The failure members of the union
 * come back as `{ kind: 'error' }` so the form can show the server's message.
 */
export const useVendorPromotionMarketingUpdate = ({
  vendorId,
}: UseVendorPromotionMarketingUpdateParams): UseVendorPromotionMarketingUpdateResult => {
  const [mutate, { loading }] =
    useSuppliersPageVendorPromotionMarketingUpdateMutation();

  const save = useCallback(
    async (
      promotionId: string,
      translations: VendorPromotionMarketingTranslationInput[]
    ): Promise<MarketingUpdateResult> => {
      if (!vendorId) {
        return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
      }

      try {
        const response = await mutate({
          variables: { input: { vendorId, promotionId, translations } },
        });
        const result = response.data?.vendorPromotionMarketingUpdate;

        if (result?.__typename === 'VendorPromotionMarketingUpdateResult') {
          return {
            kind: 'success',
            promotion: mapPromotionRecord(result.record),
          };
        }

        return {
          kind: 'error',
          message: result?.message ?? DEFAULT_ERROR_MESSAGE,
        };
      } catch (error) {
        return {
          kind: 'error',
          message:
            error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
        };
      }
    },
    [mutate, vendorId]
  );

  return { save, isSaving: loading };
};
